import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { 
  FaPaperPlane, 
  FaRobot, 
  FaUser,
  FaSpinner,
  FaTrashAlt
} from "react-icons/fa";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import { 
  selectChatMessages, 
  selectChatLoading, 
  addChatMessage, 
  updateLastAgentMessage, 
  setChatLoading,
  fetchRecommendations,
  selectDrawerOpen,
  clearChatHistory
} from "../agentSlice";
import { agentAPI, consumeSSEStream } from "../agentAPI";
import ProgressSteps from "./ProgressSteps";

const safeRender = (val) => {
  if (typeof val === "string" || typeof val === "number") return val;
  if (typeof val === "object" && val !== null) return val.title || val.text || val.recommendation || JSON.stringify(val);
  return "";
};

export const AgentChat = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const messages = useSelector(selectChatMessages);
  const isTyping = useSelector(selectChatLoading);
  const sessionUser = useSelector(selectLogInUsername);
  const clientId = sessionUser ? sessionUser.split("@")[0] : null;
  const isDrawerOpen = useSelector(selectDrawerOpen);

  const [isOpen, setIsOpen] = useState(true);
  const [inputVal, setInputVal] = useState("");
  const [hasContext, setHasContext] = useState(false);
  const [queueText, setQueueText] = useState("");
  const messagesEndRef = useRef(null);

  // Queue position polling
  useEffect(() => {
    let intervalId;
    if (isTyping && clientId) {
      const fetchQueueStatus = async () => {
        try {
          const res = await agentAPI.getQueueStatus();
          const status = res.data;
          if (status) {
            const queued = status.queued || [];
            const active = status.active;
            
            // Check if we are active or queued
            if (active && active.client === clientId) {
              setQueueText("Processing your request...");
            } else {
              const myIndex = queued.findIndex(q => q.client === clientId);
              if (myIndex >= 0) {
                const ahead = myIndex;
                if (ahead === 0) {
                  setQueueText("Your question is next in line — processing soon");
                } else {
                  setQueueText(`Your question is in queue — ${ahead} request${ahead > 1 ? "s" : ""} ahead of you`);
                }
              } else {
                if (queued.length > 0) {
                  setQueueText(`Queue is busy — ${queued.length} request${queued.length > 1 ? "s" : ""} in line`);
                } else {
                  setQueueText("");
                }
              }
            }
          }
        } catch (err) {
          console.error("Error fetching queue status:", err);
        }
      };
      
      fetchQueueStatus();
      intervalId = setInterval(fetchQueueStatus, 5000);
    } else {
      setQueueText("");
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isTyping, clientId]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Handle body active class toggling
  useEffect(() => {
    if (isOpen && !isDrawerOpen) {
      document.body.classList.add("ai-agent-active");
    } else {
      document.body.classList.remove("ai-agent-active");
    }
    return () => {
      document.body.classList.remove("ai-agent-active");
    };
  }, [isOpen, isDrawerOpen]);

  const handleSend = async (e, customText = null) => {
    if (e) e.preventDefault();
    const queryText = (customText || inputVal).trim();
    if (!queryText || !clientId || isTyping) return;

    setInputVal("");

    // 1. Add user message
    dispatch(addChatMessage({
      id: `user_${Date.now()}`,
      sender: "user",
      text: queryText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));

    // 2. Start agent response state with a placeholder message
    dispatch(setChatLoading(true));
    const agentMsgId = `agent_${Date.now()}`;
    dispatch(addChatMessage({
      id: agentMsgId,
      sender: "agent",
      text: "",
      isStreaming: true,
      progressStep: 0,
      progressMessage: "Starting ask...",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));

    let accumulatedText = "";

    try {
      const streamUrl = agentAPI.streamAsk();
      await consumeSSEStream(streamUrl, {
        method: "POST",
        body: {
          collectionName: clientId,
          question: queryText,
          currentPage: location.pathname
        },
        onProgress: (step, message) => {
          dispatch(updateLastAgentMessage({
            progressStep: step,
            progressMessage: message,
          }));
        },
        onResult: (data) => {
          accumulatedText = data.answer || "No reply received.";
          const followUps = data.followUpQuestions || [];
          if (data.hasContext) {
            setHasContext(true);
          }
          dispatch(updateLastAgentMessage({
            text: accumulatedText,
            followUpQuestions: followUps,
            isStreaming: false,
            progressStep: null,
            progressMessage: null,
          }));
          dispatch(setChatLoading(false));
          dispatch(fetchRecommendations(clientId));
        },
        onError: (errorMsg) => {
          dispatch(updateLastAgentMessage({
            text: `Error: ${errorMsg}`,
            isError: true,
            isStreaming: false,
          }));
          dispatch(setChatLoading(false));
        }
      });
    } catch (err) {
      console.error("Chat error:", err);
      dispatch(updateLastAgentMessage({
        text: `Failed to communicate with agent. ${err.message || ""}`,
        isStreaming: false,
      }));
      dispatch(setChatLoading(false));
    }
  };

  const handleClearHistory = () => {
    dispatch(clearChatHistory());
    setHasContext(false);
    agentAPI.clearConversation(clientId);
  };

  const SUGGESTED_QUESTIONS = [
    "What is my biggest conversion problem?",
    "Which pages need the most attention?",
    "How are my nudges performing?",
    "Where are users dropping off?",
  ];

  if (!clientId || isDrawerOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] font-sans">


      {/* Floating Chat Panel */}
      {isOpen && (
        <div className="w-[360px] h-[480px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
          
          {/* Header */}
          <div className="bg-slate-900 dark:bg-slate-950 p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center">
                <FaRobot className="text-white text-base" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">AI Copilot</h4>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                  <span className="text-[10px] text-slate-300">Ready to assist</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button
                  onClick={handleClearHistory}
                  className="text-slate-400 hover:text-rose-400 p-1.5 rounded-lg hover:bg-slate-800/80 transition-colors"
                  title="Clear Chat"
                >
                  <FaTrashAlt className="text-xs" />
                </button>
              )}

            </div>
          </div>

          {/* Context Memory Indicator */}
          {hasContext && messages.length > 0 && (
            <div className="px-4 py-1 bg-slate-100 dark:bg-slate-800/50 border-b border-slate-200/60 dark:border-slate-800 flex items-center gap-1 shrink-0">
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                🧠 Remembers previous messages
              </span>
            </div>
          )}

          {/* Messages History */}
          <div className="flex-grow p-4 overflow-y-auto space-y-4 bg-slate-50 dark:bg-slate-950/20 scrollbar-thin">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4 text-slate-400">
                <div className="p-3.5 rounded-full bg-slate-100 dark:bg-slate-800/50 mb-3">
                  <FaRobot className="text-2xl text-slate-500 dark:text-slate-400" />
                </div>
                <h5 className="font-bold text-slate-700 dark:text-slate-200 text-sm mb-1">
                  Ask me anything!
                </h5>
                <p className="text-xs max-w-[200px] leading-relaxed mb-4">
                  How can I help optimize your dashboard metrics today?
                </p>
                {/* Suggested Questions */}
                <div className="space-y-2 w-full text-left">
                  <p className="text-slate-500 dark:text-slate-400 text-[11px] font-semibold px-1">Try asking:</p>
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleSend(null, q)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400 hover:border-cyan-200 transition-colors"
                    >
                      💬 {safeRender(q)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg) => {
                const isAgent = msg.sender === "agent";
                return (
                  <div key={msg.id} className={`flex items-start gap-2.5 ${isAgent ? "" : "flex-row-reverse"}`}>
                    <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold ${
                      isAgent ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300" : "bg-slate-800 text-white"
                    }`}>
                      {isAgent ? <FaRobot /> : <FaUser />}
                    </div>

                    <div className={`flex flex-col max-w-[75%] ${isAgent ? "items-start" : "items-end"}`}>
                      <div className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm ${
                        isAgent 
                          ? "bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-none" 
                          : "bg-slate-900 dark:bg-cyan-600 text-white rounded-tr-none"
                      }`}>
                        {msg.isStreaming ? (
                          <div className="space-y-2">
                            <ProgressSteps
                              currentStep={msg.progressStep}
                              currentMessage={msg.progressMessage}
                            />
                            {queueText && (
                              <div className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold bg-slate-50 dark:bg-slate-950 p-2 rounded border border-slate-200/50 dark:border-slate-800/50 flex items-center gap-1.5 animate-pulse">
                                <span>⏳</span> {queueText}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p style={{ whiteSpace: "pre-wrap" }}>
                            {safeRender(msg.text) || (
                              <div className="flex items-center gap-1.5 py-0.5">
                                <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </div>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Follow up questions block */}
                      {isAgent && msg.followUpQuestions && msg.followUpQuestions.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5 w-full">
                          {msg.followUpQuestions.map((q, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSend(null, typeof q === "string" ? q : q?.text || q?.question)}
                              className="text-left px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-cyan-300 dark:hover:border-cyan-500 hover:bg-cyan-50/50 dark:hover:bg-cyan-950/20 text-[10px] text-slate-600 dark:text-slate-400 hover:text-cyan-700 dark:hover:text-cyan-400 transition-all duration-200 font-semibold bg-white/80 dark:bg-slate-900/80 shadow-sm"
                            >
                              💬 {typeof q === "string" ? q : q?.text || q?.question || "Suggested Action"}
                            </button>
                          ))}
                        </div>
                      )}

                      <span className="text-[9px] text-slate-400 mt-1 px-1">
                        {msg.timestamp}
                      </span>
                    </div>
                  </div>
                );
              })
            )}

            {isTyping && messages[messages.length - 1]?.text !== "" && (
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300">
                  <FaRobot />
                </div>
                <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 shadow-sm rounded-tl-none">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 py-0.5">
                      <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    {queueText && (
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold bg-slate-50 dark:bg-slate-950 p-2 rounded border border-slate-200/50 dark:border-slate-800/50 flex items-center gap-1.5 animate-pulse">
                        <span>⏳</span> {queueText}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form 
            onSubmit={handleSend}
            className="p-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2"
          >
            <input
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder={isTyping ? "Still thinking..." : "Ask a question..."}
              disabled={isTyping}
              className="flex-grow px-3.5 py-2.5 bg-slate-100 dark:bg-slate-950 border-0 outline-none rounded-xl text-xs dark:text-slate-100 placeholder-slate-400 disabled:opacity-60 focus:ring-1 focus:ring-cyan-500 transition-all"
            />
            <button
              type="submit"
              disabled={!inputVal.trim() || isTyping}
              className="px-3.5 py-2.5 bg-slate-900 hover:bg-slate-800 dark:bg-cyan-600 dark:hover:bg-cyan-500 text-white rounded-xl shadow-sm hover:shadow disabled:opacity-40 disabled:hover:bg-slate-900 transition-all shrink-0 flex items-center justify-center min-w-[80px]"
            >
              {isTyping ? (
                <div className="flex items-center gap-1.5">
                  <FaSpinner className="animate-spin text-xs" />
                  <span className="text-[10px] font-semibold">Thinking...</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <FaPaperPlane className="text-xs" />
                  <span className="text-[10px] font-semibold">Send</span>
                </div>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
export default AgentChat;
